import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { csrfToken } from '@/services/api';
import type { PageViewTarget } from '@/features/common/types/PageViewTarget';

/** Kept in step with PageViewController.PATH. */
export const PAGE_VIEW_ENDPOINT = '/api/v1/public/metrics/view';

/** Kept in step with PageViewController.CABINET_PATH. */
export const CABINET_PAGE_VIEW_ENDPOINT = '/api/v1/metrics/view';

/**
 * Where each zone reports, and what that costs it.
 *
 * <p>The cabinet has an endpoint of its own (T236) rather than a field in the body, because the two
 * differ in the one thing a path is for: its own is behind a session, which is what lets a visit be
 * counted as a session and not only as a hit. Being outside `/api/v1/public/**` it also keeps that
 * prefix's two exemptions — it wants a signed-in caller, and it wants the CSRF token, which this
 * report therefore sends.
 */
export const PAGE_VIEW: Record<'public' | 'cabinet', PageViewTarget> = {
  public: { endpoint: PAGE_VIEW_ENDPOINT, csrf: false },
  cabinet: { endpoint: CABINET_PAGE_VIEW_ENDPOINT, csrf: true },
};

/**
 * Sends one page view. Never throws and never rejects — see the hook below for why.
 *
 * Separate from the hook so it can be tested without rendering: what matters is the request it
 * makes and that nothing it does can escape into the page.
 */
export function reportPageView(pathname: string, target: PageViewTarget = PAGE_VIEW.public): void {
  // A bare fetch rather than apiPost: this is telemetry, and nothing about counting a page view
  // should be able to reach the visitor. apiPost throws an ApiError on any non-2xx and routes a
  // 401 to the session-expiry handler, so a rate-limited or failed report would surface as an
  // application error on a page that is working perfectly well.
  try {
    const token = target.csrf ? csrfToken() : undefined;
    void fetch(target.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'X-XSRF-TOKEN': token } : {}),
      },
      body: JSON.stringify({ path: pathname }),
      // The report is sent as the visitor moves on; keepalive lets it complete if they move on
      // again immediately, or close the tab.
      keepalive: true,
    }).catch(() => {
      // A visitor who cannot be counted still gets their page.
    });
  } catch {
    // fetch itself can throw synchronously where it is unavailable or blocked.
  }
}

/**
 * Reports every page the public zone renders, so per-page usage counting survives the React
 * migration (FR-038).
 *
 * The backend counts one row per HTTP request, with the URI as the page — which was every page
 * a visitor opened while the site was rendered on the server. It is not any more: moving from
 * the home page to news to the map never touches the server for the document, so all three were
 * recorded as whichever page the visitor entered on. Per-module counting still works, because
 * the API calls each page makes carry the module in their path, but "which pages were read" had
 * quietly become "which page was linked to".
 *
 * The page a visit starts on is reported too. It used to be skipped as already counted by the
 * servlet filter, which holds only where the backend serves the document: behind nginx it is
 * served without reaching the application, so that page was counted by neither. The filter now
 * leaves public pages to this report — the two change together, or entry pages are doubled.
 *
 * <p>Both zones report through it since T236: the filter stopped counting /app/** and the XHRs
 * behind it, since an XHR is not a page and one dossier is not a page apart from the next, so the
 * cabinet is counted by the shells or not at all. What is sent is the raw pathname either way —
 * the server decides which routes exist and folds the dossiers into one path, so a caller that
 * tidied its own path would be reporting a page the vocabulary does not have.
 */
export function usePageViewCounter(target: PageViewTarget = PAGE_VIEW.public) {
  const { pathname } = useLocation();
  // StrictMode runs a mount effect twice in development; without this the entry page would be
  // reported twice there.
  const reportedPath = useRef<string | null>(null);

  useEffect(() => {
    if (reportedPath.current === pathname) {
      return;
    }
    reportedPath.current = pathname;
    reportPageView(pathname, target);
  }, [pathname, target]);
}
